import { ActionConsts } from '@Definitions/index';
import { IAppointments } from '@Reducers/checkout';
import { IClinicLocationsData } from '@Reducers/schedule';
import { CONSULTATION_CODE_DEFAULT, CONSULTATION_NAME_DEFAULT, ConsultationMode, ItemType } from '@Utils/constants';
import { InvoiceStatus } from '@Utils/Helpers/Order/helper';

export interface IPatientInvoice {
  id?: string;
  status?: string;
  encounterId?: string;
  createdAt?: string;
  items?: {
    id: string;
    type: string;
    quantity: number;
    typeId: string;
  }[];
}
export interface IConsultationData {
  consultationTypeId: string;
  name: string;
  description: string;
  consultationCode: string;
  amount: number;
  isBookable: boolean;
  duration?: number;
  originalAmount?: number;
  isLoading?: boolean;
}

export interface IConsultationOrders {
  counsellingOrders?: IConsultationOrder[];
  followupOrders?: IConsultationOrder[];
  patientQueryOrders?: IConsultationOrder[];
  reportReadingOrders?: IConsultationOrder[];
  screeningCallOrders?: IConsultationOrder[];
}

export interface IDeliveryEstimate {
  courier_name: string;
  courier_id: number;
  expected_pickup: string;
  chargeable_weight: number;
  charges: number;
  etd: string;
}

export interface IOrder {
  consultationData: IConsultationData;
  orderConfirmed: boolean;
  consultationOrders?: IConsultationOrders;
  pendingOrders?: Record<ItemType, IPendingOrders>;
  patientInvoice?: IPatientInvoice;
  isLoading: boolean;
  locationId?: string;
  locationData?: IClinicLocationsData;
  deliveryEstimate?: IDeliveryEstimate;
}

export interface IPendingOrders {
  length: number;
  map(arg0: (article: any) => JSX.Element): import('react').ReactNode | Iterable<import('react').ReactNode>;
  count: number;
  encounterIds: string[];
  lastEncounterId: string;
  lastEncounterOrders: any[];
}

export interface IDrugOrder {
  invoiceId: string;
  status: string;
  vendor: string;
  shippingOrderId: string;
  address: string;
  pincode: string;
  trackingUrl: string;
  isCod: boolean;
  estimatedDate?: string;
  timeStamps: {
    prescribed?: string;
    received?: string;
    packed?: string;
    dispatched?: string;
    delivered?: string;
  };
  drugs: {
    id: string;
    name: string; // name here will be combination of drug name and salt in brackets
    quantity: number;
  }[];
}

export enum Fulfillment {
  PENDING = 'pending',
  COLLECTED = 'collected',
  SAMPLE_SYNCED = 'sampleSynced',
  REPORT_SHARED = 'reportShared',
  SCHEDULED = 'scheduled',
  CANCELLED = 'cancelled',
  DELAYED = 'reportGenerationDelayed',
}

export interface IConsultationOrder {
  serialNo: number;
  consultationId: string;
  consultationTypeId: string;
  status: string;
  invoiceItemId: string;
  mode: ConsultationMode;
  invoiceStatus: InvoiceStatus;
  consultationType: {
    name: string;
    code: string;
    duration: number;
    sellingPrice: number;
    listingPrice: number;
  };
  dueDate: string;
  appointmentData: IAppointments;
  timestamps: {
    prescribed?: string;
    sessionDate?: string;
  };
}

export interface IPendingOrders {
  count: number;
  encounterIds: string[];
  lastEncounterId: string;
  lastEncounterOrders: any[];
}

const DEFAULT = {
  consultationData: {
    consultationTypeId: '',
    consultationCode: CONSULTATION_CODE_DEFAULT,
    name: CONSULTATION_NAME_DEFAULT,
    description: '',
    amount: undefined,
    isBookable: true,
    duration: undefined,
    isLoading: undefined,
  },
};

const INITIAL_STATE: IOrder = {
  consultationData: DEFAULT.consultationData,
  orderConfirmed: undefined,
  consultationOrders: {
    counsellingOrders: [],
    followupOrders: [],
    patientQueryOrders: [],
    reportReadingOrders: [],
  },
  patientInvoice: {},
  isLoading: false,
  locationId: '',
};

export const OrderReducer = (state = INITIAL_STATE, action: any) => {
  switch (action.type) {
    case ActionConsts.Order.ResetOrderData:
      return {
        ...state,
        orderConfirmed: INITIAL_STATE.orderConfirmed,
        isLoading: false,
      };
    case ActionConsts.Order.ResetConsultationData:
      return {
        ...state,
        consultationData: INITIAL_STATE.consultationData,
        locationId: INITIAL_STATE.locationId,
        isLoading: false,
      };
    case ActionConsts.Order.GetConsultationDetailsInit:
      return {
        ...state,
        consultationData: {
          ...INITIAL_STATE.consultationData,
          isLoading: true,
        },
        locationData: INITIAL_STATE.locationData,
        locationId: INITIAL_STATE.locationId,
        isLoading: true,
      };
    case ActionConsts.Order.GetConsultationDetailsSuccess:
      return {
        ...state,
        consultationData: {
          ...action.consultationData,
          isLoading: false,
        },
        locationData: action.locationData,
        locationId: action.locationId,
        isLoading: false,
      };
    case ActionConsts.Order.GetConsultationDetailsFail:
      return {
        ...state,
        consultationData: {
          ...DEFAULT.consultationData,
          isLoading: false,
        },
        locationData: INITIAL_STATE.locationData,
        locationId: INITIAL_STATE.locationId,
        isLoading: false,
      };
    case ActionConsts.Order.UpdateOrderConfirmed:
      return {
        ...state,
        orderConfirmed: action.orderConfirmed,
        isLoading: true,
      };
    case ActionConsts.Order.ResetPatientInvoices:
      return {
        ...state,
        patientInvoice: INITIAL_STATE.patientInvoice,
        isLoading: false,
      };
    case ActionConsts.Order.GetConsultationOrdersInit:
      return {
        ...state,
        consultationOrders: INITIAL_STATE.consultationOrders,
        isLoading: true,
      };
    case ActionConsts.Order.GetConsultationOrdersSuccess:
      return {
        ...state,
        consultationOrders: action.consultationOrders,
        isLoading: false,
      };
    case ActionConsts.Order.ResetConsultationOrders:
    case ActionConsts.Order.GetConsultationOrdersFail:
      return {
        ...state,
        consultationOrders: INITIAL_STATE.consultationOrders,
        isLoading: false,
      };
    case ActionConsts.Order.GetPendingOrdersInit:
    case ActionConsts.Order.GetPendingOrdersInit:
      return {
        ...state,
        pendingOrders: INITIAL_STATE.pendingOrders,
        isLoading: true,
      };
    case ActionConsts.Order.GetPendingOrdersSuccess:
      return {
        ...state,
        pendingOrders: action.pendingOrders,
        isLoading: false,
      };
    case ActionConsts.Order.GetPendingOrdersFail:
      return {
        ...state,
        pendingOrders: INITIAL_STATE.pendingOrders,
        isLoading: false,
      };
    case ActionConsts.Order.GetDeliveryEstimatesInit:
      return {
        ...state,
        deliveryEstimate: INITIAL_STATE.deliveryEstimate,
        isLoading: true,
      };
    case ActionConsts.Order.GetDeliveryEstimatesSuccess:
      return {
        ...state,
        deliveryEstimate: action.deliveryEstimate,
        isLoading: false,
      };
    case ActionConsts.Order.GetDeliveryEstimatesFail:
      return {
        ...state,
        deliveryEstimate: INITIAL_STATE.deliveryEstimate,
        isLoading: false,
      };
    default:
      return state;
  }
};
